import { useCallback, useState } from 'react'
import { GoogleAuthProvider, reauthenticateWithPopup } from 'firebase/auth'
import { auth } from '@/shared/firebase/client'
import { formatDate } from '@/shared/util/dates'
import './CompetitorAnalysis.css'

/**
 * The weekly competitor analysis — 21 Oct 2026.
 *
 * The Monday "Market research" routine rewrites `Konkurrentanalyse-current.html`
 * in the shared drive (Mountainsnow - Shared internal → Market → Competitors),
 * so this screen looks the file up by name each time rather than by id, and
 * always shows the latest.
 *
 * Read with the viewer's own Google account. The CRM asks for read-only Drive
 * access when the button is pressed and Drive decides — anybody who cannot open
 * the folder in Drive cannot open it here either, and no server holds a key to
 * the drive. The token lives in this tab's memory only.
 *
 * The page is the routine's own self-contained HTML, with its scripts, so it is
 * shown in a sandboxed frame without same-origin: it runs, and it cannot reach
 * the CRM, its storage or its sign-in.
 */

const FOLDER_ID = '1T5vbKNSM1SKWDZoQLK1EtP5LbAe56pXW'
const CURRENT = 'Konkurrentanalyse-current.html'
const DRIVE = 'https://www.googleapis.com/drive/v3/files'

let cached: { token: string; until: number } | null = null

interface Report {
  html: string
  name: string
  modifiedTime: string
  webViewLink: string
}

async function driveToken(): Promise<string> {
  if (cached && cached.until > Date.now()) return cached.token
  const user = auth.currentUser
  if (!user) throw new Error('Sign in first.')
  const provider = new GoogleAuthProvider()
  provider.addScope('https://www.googleapis.com/auth/drive.readonly')
  if (user.email) provider.setCustomParameters({ login_hint: user.email })
  const result = await reauthenticateWithPopup(user, provider)
  const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken
  if (!token) throw new Error('Google did not hand over Drive access.')
  // Google's tokens last an hour; a little under, so one never expires mid-read.
  cached = { token, until: Date.now() + 50 * 60_000 }
  return token
}

async function driveJson(url: string, token: string) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
    if (res.status === 401) cached = null
    throw new Error(body?.error?.message ?? `Drive answered ${res.status}.`)
  }
  return res
}

async function loadReport(): Promise<Report> {
  const token = await driveToken()
  const q = `'${FOLDER_ID}' in parents and name contains 'Konkurrentanalyse' and trashed = false`
  const list = (await (
    await driveJson(
      `${DRIVE}?q=${encodeURIComponent(q)}&orderBy=modifiedTime desc&pageSize=20` +
        '&fields=files(id,name,modifiedTime,webViewLink)&supportsAllDrives=true&includeItemsFromAllDrives=true',
      token,
    )
  ).json()) as { files: { id: string; name: string; modifiedTime: string; webViewLink: string }[] }
  // The current file when it is there; otherwise the newest dated copy.
  const file = list.files.find((f) => f.name === CURRENT) ?? list.files[0]
  if (!file) throw new Error('No competitor analysis was found in the Competitors folder.')
  const html = await (await driveJson(`${DRIVE}/${file.id}?alt=media&supportsAllDrives=true`, token)).text()
  return { html, name: file.name, modifiedTime: file.modifiedTime, webViewLink: file.webViewLink }
}

export function CompetitorAnalysis() {
  const [report, setReport] = useState<Report | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = useCallback(() => {
    setBusy(true)
    setError(null)
    loadReport()
      .then(setReport)
      .catch((e: unknown) => {
        const code = (e as { code?: string }).code ?? ''
        setError(
          code === 'auth/popup-blocked'
            ? 'Your browser blocked the Google popup. Allow popups for this site and try again.'
            : code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request'
              ? null
              : code === 'auth/user-mismatch'
                ? 'Choose the same Google account you are signed in to the CRM with.'
                : e instanceof Error
                  ? e.message
                  : String(e),
        )
      })
      .finally(() => setBusy(false))
  }, [])

  return (
    <div className="compa">
      <div className="compa__head">
        <div>
          <h2 className="compa__title">Competitor analysis</h2>
          <p className="compa__lede">
            {report
              ? `Updated ${formatDate(report.modifiedTime)} · ${report.name}`
              : 'Updated every Monday by the market research routine, read from Google Drive.'}
          </p>
        </div>
        <div className="compa__acts">
          {report && (
            <a className="btn btn--ghost" href={report.webViewLink} target="_blank" rel="noreferrer">
              Open in Drive
            </a>
          )}
          <button className="btn btn--primary" onClick={open} disabled={busy}>
            {busy ? 'Loading…' : report ? 'Reload' : 'Show the analysis'}
          </button>
        </div>
      </div>

      {error && <p className="recform__error">{error}</p>}

      {report ? (
        <iframe
          className="compa__frame"
          title="Competitor analysis"
          srcDoc={report.html}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        />
      ) : (
        !busy && (
          <p className="empty-note">
            Google asks once for read-only access to Drive, so the CRM can open the file as you. It
            only works for accounts that can open the Competitors folder in the shared drive.
          </p>
        )
      )}
    </div>
  )
}
